import { useCallback } from "react";
import { useSelector } from "react-redux";
import type * as React from "react";
import { Box } from "@mui/material";
import { AxiosError } from "axios";
import { SurveyModel } from "survey-core";
import { createTestRedirect } from "apis/v3/orderApi";
import FormTemplateWrapper from "components/v3/sharedComponents/forms/FormTemplateWrapper";
import { COLOGUARD_TEST_ID } from "components/v3/pages/CologuardLaunchPage/constants";
import { COLORS } from "components/v3/Theme/colors";
import { FORM_IDS } from "constants/v3/formConstants";
import { selectUserPersonalInformation } from "store/reducers/user/UserAccountSlice";

type CologuardAcknowledgementFormProps = {
  onCancel: () => void;
  onComplete: () => void;
};

const surveyTextValue = (value: unknown, fallback = ""): string => {
  if (typeof value === "string") {
    return value.trim();
  }
  return fallback.trim();
};

const toDateInputValue = (dob?: string): string => {
  if (!dob) {
    return "";
  }
  // Accept ISO or YYYY-MM-DD; date inputs need YYYY-MM-DD.
  if (/^\d{4}-\d{2}-\d{2}/.test(dob)) {
    return dob.slice(0, 10);
  }
  const parsed = new Date(dob);
  if (Number.isNaN(parsed.getTime())) {
    return "";
  }
  return parsed.toISOString().slice(0, 10);
};

const CologuardAcknowledgementForm: React.FC<CologuardAcknowledgementFormProps> = ({
  onCancel,
  onComplete
}) => {
  const { firstName, lastName, dob } = useSelector(selectUserPersonalInformation);

  const handleSurveyInstantiated = useCallback(
    (survey: SurveyModel) => {
      // Prefill identity fields; leave editable (not readOnly/disabled).
      if (firstName) {
        survey.setValue("first-name", firstName);
      }
      if (lastName) {
        survey.setValue("last-name", lastName);
      }
      const dobValue = toDateInputValue(dob);
      if (dobValue) {
        survey.setValue("date-of-birth", dobValue);
      }
    },
    [dob, firstName, lastName]
  );

  const handleComplete = useCallback(
    async (survey: SurveyModel) => {
      const submittedFirstName = surveyTextValue(
        survey.getValue("first-name"),
        firstName
      );
      const submittedLastName = surveyTextValue(
        survey.getValue("last-name"),
        lastName
      );

      if (!submittedFirstName || !submittedLastName) {
        throw new Error("Member name is required");
      }

      const response = await createTestRedirect({
        firstName: submittedFirstName,
        lastName: submittedLastName,
        testId: COLOGUARD_TEST_ID
      });

      if (response instanceof AxiosError) {
        throw response;
      }

      onComplete();
    },
    [firstName, lastName, onComplete]
  );

  return (
    <Box
      sx={{
        width: "100%",
        "& .sd-html p, & .sd-html li": {
          color: `${COLORS.BLACK} !important`
        },
        "& .cologuard-release-note, & .cologuard-release-note *": {
          color: `${COLORS.DARK_GRAY} !important`
        },
        "& .gl-button.Mui-disabled, & .gl-button.Mui-disabled .MuiTypography-root, & .gl-button.Mui-disabled .MuiSvgIcon-root":
          {
            color: `${COLORS.WHITE} !important`
          },
        "& .sd-question__required-text, & .sd-title .sd-question__required-text":
          {
            color: "#E50808 !important"
          },
        "& .sd-row:has([data-name='date-of-birth'])": {
          overflow: "visible",
          paddingBottom: "16px"
        },
        "& [data-name='date-of-birth-spacer'], & [data-name='date-of-birth-spacer'] *":
          {
            pointerEvents: "none",
            visibility: "hidden"
          }
      }}
    >
      <FormTemplateWrapper
        formId={FORM_IDS.COLOGUARD_ACKNOWLEDGEMENT}
        cancelButtonText="Take me back"
        onCancel={onCancel}
        onComplete={handleComplete}
        onSurveyInstantiated={handleSurveyInstantiated}
        overrideNextButtonEnabled={true}
        showNavigationButtons={true}
      />
    </Box>
  );
};

export default CologuardAcknowledgementForm;
